/**
 * The Gravatar picture for an email address, through Outlaw Sign-in's avatar proxy (OWA-1184,
 * `/signin/avatar/<sha256>`). The browser never contacts Gravatar, and the proxy answers a
 * transparent image when the address has none, so the initials underneath show through.
 *
 * @module client/Auth/signInAvatar
 */

import IDP_CONFIG, {type IdpProvider} from './idp-config';
import {signInHost} from './signInMethods';

/** Gravatar's hash input: the address trimmed and lowercased. */
export async function emailHash(email: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** The proxy URL for `email`, or null when there is no address to look up. Only Outlaw Sign-in
 * serves the proxy, so Auth0 builds (whose sign-in host is Auth0) have no Gravatar here. */
export async function signInAvatarUrl(
    email: string | null | undefined, host = signInHost(), provider: IdpProvider = IDP_CONFIG.provider,
): Promise<string | null> {
    if (provider !== 'zitadel' || !email || !email.includes('@')) return null;
    return `https://${host}/signin/avatar/${await emailHash(email)}`;
}
